/**
 * Remote Config Service
 * Handles proper initialization and management of Firebase Remote Config
 */

import { FeatureFlagName } from '@curbside-quest/shared';
import { conditionalLog, conditionalWarn, conditionalError } from '../utils/logger';

// Environment detection - must match exactly what's in config.ts
const isLocalDevelopment = import.meta.env.DEV && (
  !import.meta.env.VITE_FIREBASE_PROJECT_ID || 
  import.meta.env.VITE_FIREBASE_PROJECT_ID.startsWith('demo-')
);

const shouldUseRemoteConfig = !isLocalDevelopment;

interface RemoteConfigState {
  service: any | null;
  isInitialized: boolean;
  isInitializing: boolean;
  error: Error | null;
  fetchAndActivate: any | null;
  getValue: any | null;
}

class RemoteConfigService {
  private static instance: RemoteConfigService;
  private state: RemoteConfigState = {
    service: null,
    isInitialized: false,
    isInitializing: false,
    error: null,
    fetchAndActivate: null,
    getValue: null
  };
  private initPromise: Promise<void> | null = null;
  private listeners: Array<() => void> = [];

  private constructor() {
    conditionalLog('🔧 RemoteConfigService: Constructor called', {
      isLocalDevelopment,
      shouldUseRemoteConfig
    });
  }

  static getInstance(): RemoteConfigService {
    if (!RemoteConfigService.instance) {
      RemoteConfigService.instance = new RemoteConfigService();
    }
    return RemoteConfigService.instance;
  }

  /**
   * Initialize Remote Config service
   */
  async initialize(app: any): Promise<void> {
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = this._initialize(app);
    return this.initPromise;
  }

  private async _initialize(app: any): Promise<void> {
    if (this.state.isInitialized || this.state.isInitializing) {
      return;
    }

    conditionalLog('🔧 RemoteConfigService: Starting initialization...', {
      shouldUseRemoteConfig,
      isLocalDevelopment
    });

    if (!shouldUseRemoteConfig) {
      conditionalLog('🏁 RemoteConfigService: Skipping Remote Config (local development)');
      this.state.isInitialized = true;
      this.notifyListeners();
      return;
    }

    this.state.isInitializing = true;

    try {
      conditionalLog('🔧 RemoteConfigService: Loading Remote Config module...');
      
      // Dynamic import of Remote Config
      const remoteConfigModule = await import('firebase/remote-config');
      const { getRemoteConfig, fetchAndActivate, getValue } = remoteConfigModule;

      conditionalLog('✅ RemoteConfigService: Module loaded, creating service...');

      // Create Remote Config service
      this.state.service = getRemoteConfig(app);
      this.state.fetchAndActivate = fetchAndActivate;
      this.state.getValue = getValue;

      // Validate service
      if (!this.state.service) {
        throw new Error('Remote Config service creation failed');
      }

      conditionalLog('✅ RemoteConfigService: Service created, configuring...');

      // Configure Remote Config with shorter fetch interval for debugging
      // Use shorter intervals in non-production environments for faster testing
      const fetchInterval = import.meta.env.DEV ? 60000 : (import.meta.env.MODE === 'production' ? 300000 : 60000); // 5min prod, 1min others
      this.state.service.settings.minimumFetchIntervalMillis = fetchInterval;
      
      conditionalLog('🔧 RemoteConfigService: Fetch interval set to:', fetchInterval, 'ms', `(${fetchInterval/1000}s)`);

      // Set default values as strings
      this.state.service.defaultConfig = {
        AI_ANALYSIS_ENABLED: 'false',
        AI_SIMULATE_FAILURE: 'false',
        AI_SERVICE_UNAVAILABLE: 'false',
        ADVANCED_SEARCH_ENABLED: 'true',
        PREMIUM_FEATURES_ENABLED: 'true',
        DEBUG_MODE_ENABLED: import.meta.env.DEV ? 'true' : 'false',
        MAINTENANCE_MODE: 'false',
        CONSOLE_LOGGING_ENABLED: import.meta.env.DEV ? 'true' : 'false',
        ACTION_COMPONENTS_ENABLED: import.meta.env.DEV ? 'true' : 'false',
        ACTION_CARDS_ENABLED: import.meta.env.DEV ? 'true' : 'false',
        ACTION_BUTTONS_ENABLED: import.meta.env.DEV ? 'true' : 'false',
        COMMUNITY_UPDATES_ENABLED: import.meta.env.DEV ? 'true' : 'false'
      };

      conditionalLog('✅ RemoteConfigService: Configuration complete', {
        fetchInterval: this.state.service.settings.minimumFetchIntervalMillis,
        defaultConfigKeys: Object.keys(this.state.service.defaultConfig)
      });

      this.state.isInitialized = true;
      this.state.isInitializing = false;
      this.state.error = null;

      conditionalLog('✅ RemoteConfigService: Initialization complete');
      
      // Force an immediate fetch on first initialization to ensure fresh values
      conditionalLog('🔧 RemoteConfigService: Performing initial fetch to ensure fresh values...');
      try {
        await this.forceRefresh();
        conditionalLog('✅ RemoteConfigService: Initial fetch complete');
      } catch (error) {
        conditionalWarn('⚠️ RemoteConfigService: Initial fetch failed, will use defaults:', error);
      }

    } catch (error) {
      conditionalError('❌ RemoteConfigService: Initialization failed:', error);
      this.state.error = error instanceof Error ? error : new Error('Remote Config initialization failed');
      this.state.isInitializing = false;
      this.state.isInitialized = false;
    }

    this.notifyListeners();
  }

  /**
   * Fetch and activate Remote Config values
   */
  async fetchAndActivate(): Promise<void> {
    if (!shouldUseRemoteConfig) {
      conditionalLog('🏁 RemoteConfigService: Fetch skipped (local development)');
      return;
    }

    if (!this.state.isInitialized) {
      throw new Error('Remote Config service not initialized');
    }

    if (!this.state.service || !this.state.fetchAndActivate) {
      throw new Error('Remote Config service or functions not available');
    }

    conditionalLog('🔧 RemoteConfigService: Fetching and activating...');
    
    // Log current fetch status before fetching
    const currentTime = Date.now();
    const timeSinceLastFetch = currentTime - this.state.service.fetchTimeMillis;
    const fetchIntervalMs = this.state.service.settings.minimumFetchIntervalMillis;
    const canFetch = timeSinceLastFetch >= fetchIntervalMs;
    
    conditionalLog('🔧 RemoteConfigService: Current fetch status:', {
      lastFetchTime: this.state.service.fetchTimeMillis,
      lastFetchStatus: this.state.service.lastFetchStatus,
      minimumFetchInterval: fetchIntervalMs,
      timeSinceLastFetch: timeSinceLastFetch,
      canFetch: canFetch,
      willFetchFresh: canFetch
    });
    
    const activated = await this.state.fetchAndActivate(this.state.service);
    
    // Log fetch results
    conditionalLog('✅ RemoteConfigService: Fetch and activate complete:', {
      activated,
      newFetchTime: this.state.service.fetchTimeMillis,
      newFetchStatus: this.state.service.lastFetchStatus,
      fetchedFreshValues: activated
    });
    
    // Log a few sample values to verify they're being fetched
    if (this.state.getValue) {
      const sampleFlags = ['AI_ANALYSIS_ENABLED', 'ADVANCED_SEARCH_ENABLED', 'PREMIUM_FEATURES_ENABLED'];
      conditionalLog('🔧 RemoteConfigService: Sample values after fetch:');
      sampleFlags.forEach(flag => {
        try {
          const value = this.state.getValue(this.state.service, flag);
          const source = value.getSource();
          const stringValue = value.asString();
          const boolValue = value.asBoolean();
          
          // Highlight if we're getting defaults instead of remote values
          const logLevel = source === 'remote' ? '✅' : '⚠️';
          conditionalLog(`  ${logLevel} ${flag}: ${stringValue} → ${boolValue} (${source})`);
        } catch (error) {
          conditionalLog(`  ❌ ${flag}: ERROR - ${error instanceof Error ? error.message : String(error)}`);
        }
      });
      
      // If all values are defaults, provide troubleshooting info
      const allSources = sampleFlags.map(flag => {
        try {
          return this.state.getValue(this.state.service, flag).getSource();
        } catch {
          return 'error';
        }
      });
      
      if (allSources.every(source => source === 'default')) {
        conditionalWarn('⚠️ RemoteConfigService: All values are from defaults, not Remote Config. This suggests:');
        conditionalWarn('  1. Remote Config parameters may not be published in Firebase Console');
        conditionalWarn('  2. Fetch interval may be preventing fresh fetch');
        conditionalWarn('  3. Network issues preventing Remote Config fetch');
        conditionalWarn('  4. Firebase project may not have Remote Config enabled');
      }
    }
  }

  /**
   * Get a feature flag value
   */
  getValue(flagName: FeatureFlagName): { asBoolean: () => boolean; asString: () => string; asNumber: () => number; getSource: () => string } {
    if (!shouldUseRemoteConfig) {
      // Return default value for local development
      const defaultValue = this.getDefaultValue(flagName);
      conditionalLog(`🏁 RemoteConfigService.getValue(${flagName}): local development = ${defaultValue} (source: local)`);
      return {
        asBoolean: () => defaultValue,
        asString: () => defaultValue.toString(),
        asNumber: () => defaultValue ? 1 : 0,
        getSource: () => 'local'
      };
    }

    if (!this.state.isInitialized || !this.state.service || !this.state.getValue) {
      // Return default value if not initialized
      const defaultValue = this.getDefaultValue(flagName);
      conditionalLog(`🏁 RemoteConfigService.getValue(${flagName}): not initialized = ${defaultValue} (source: default)`);
      return {
        asBoolean: () => defaultValue,
        asString: () => defaultValue.toString(),
        asNumber: () => defaultValue ? 1 : 0,
        getSource: () => 'default'
      };
    }

    try {
      const remoteValue = this.state.getValue(this.state.service, flagName);
      const boolValue = remoteValue.asBoolean();
      const stringValue = remoteValue.asString();
      const source = remoteValue.getSource();
      
      conditionalLog(`🏁 RemoteConfigService.getValue(${flagName}): Remote Config = ${boolValue} (string: "${stringValue}", source: ${source})`);
      
      return remoteValue;
    } catch (error) {
      conditionalWarn(`RemoteConfigService: getValue failed for ${flagName}:`, error);
      const defaultValue = this.getDefaultValue(flagName);
      conditionalLog(`🏁 RemoteConfigService.getValue(${flagName}): error fallback = ${defaultValue} (source: error)`);
      return {
        asBoolean: () => defaultValue,
        asString: () => defaultValue.toString(),
        asNumber: () => defaultValue ? 1 : 0,
        getSource: () => 'error'
      };
    }
  }

  /**
   * Get default value for a feature flag
   */
  private getDefaultValue(flagName: FeatureFlagName): boolean {
    const defaults: Record<FeatureFlagName, boolean> = {
      AI_ANALYSIS_ENABLED: import.meta.env.DEV || false,
      AI_SIMULATE_FAILURE: false,
      AI_SERVICE_UNAVAILABLE: false,
      ADVANCED_SEARCH_ENABLED: true,
      PREMIUM_FEATURES_ENABLED: true,
      DEBUG_MODE_ENABLED: import.meta.env.DEV || false,
      MAINTENANCE_MODE: false,
      CONSOLE_LOGGING_ENABLED: import.meta.env.DEV || false,
      ACTION_COMPONENTS_ENABLED: true,
      GOOGLE_ADS_ENABLED: false,
      GAMIFICATION_ENABLED: false
    };

    return defaults[flagName] || false;
  }

  /**
   * Check if Remote Config is available and initialized
   */
  isAvailable(): boolean {
    return shouldUseRemoteConfig ? this.state.isInitialized && !this.state.error : true;
  }

  /**
   * Check if Remote Config is currently initializing
   */
  isInitializing(): boolean {
    return this.state.isInitializing;
  }

  /**
   * Get current error state
   */
  getError(): Error | null {
    return this.state.error;
  }

  /**
   * Force refresh Remote Config (ignores fetch interval)
   */
  async forceRefresh(): Promise<void> {
    if (!shouldUseRemoteConfig) {
      conditionalLog('🏁 RemoteConfigService: Force refresh skipped (local development)');
      return;
    }

    if (!this.state.service) {
      throw new Error('Remote Config service not available');
    }

    conditionalLog('🔧 RemoteConfigService: Force refreshing (ignoring fetch interval)...');
    
    // Temporarily set fetch interval to 0 to force refresh
    const originalInterval = this.state.service.settings.minimumFetchIntervalMillis;
    this.state.service.settings.minimumFetchIntervalMillis = 0;
    
    try {
      await this.fetchAndActivate();
      conditionalLog('✅ RemoteConfigService: Force refresh complete');
    } finally {
      // Restore original fetch interval
      this.state.service.settings.minimumFetchIntervalMillis = originalInterval;
    }
  }

  /**
   * Get environment information
   */
  getEnvironmentInfo() {
    return {
      isLocalDevelopment,
      shouldUseRemoteConfig,
      isInitialized: this.state.isInitialized,
      isInitializing: this.state.isInitializing,
      hasError: !!this.state.error,
      error: this.state.error?.message,
      lastFetchTime: this.state.service?.fetchTimeMillis || 0,
      lastFetchStatus: this.state.service?.lastFetchStatus || 'unknown',
      fetchInterval: this.state.service?.settings?.minimumFetchIntervalMillis || 0
    };
  }

  /**
   * Add listener for state changes
   */
  addListener(callback: () => void): () => void {
    this.listeners.push(callback);
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of state changes
   */
  private notifyListeners(): void {
    this.listeners.forEach(callback => {
      try {
        callback();
      } catch (error) {
        conditionalError('RemoteConfigService: Listener error:', error);
      }
    });
  }
}

export { RemoteConfigService };